export interface GuideCareer {
  title: string
  desc: string
  salary: string
}

export interface GuideFAQ {
  q: string
  a: string
}

export interface GuideBenefit {
  icon:
    | 'ClockIcon'
    | 'TrendingUpIcon'
    | 'GlobeIcon'
    | 'BookOpenIcon'
    | 'ShieldCheckIcon'
    | 'HeartIcon'
    | 'UsersIcon'
    | 'CheckCircleIcon'
  title: string
  description: string
}

export interface GuideRelatedLink {
  label: string
  href: string
}

export interface Guide {
  slug: string
  title: string // e.g. "Online Christian Colleges"
  metaTitle: string
  metaDescription: string
  breadcrumbLabel: string
  // Hero
  heroEyebrow?: string
  heroHeadline: string
  heroDescription: string
  // API filters used to populate matched schools
  focusValue: string // maps to focusOptions value in data/degrees.ts
  defaultSpecialization?: string // optional default specialization value
  // Sidebar "what to look for" list
  sidebarChecklistTitle: string
  sidebarChecklist: string[]
  // Content sections
  overviewHeading: string
  overviewParagraphs: string[]
  benefitsHeading: string
  benefitsIntro: string
  benefits: GuideBenefit[]
  careersHeading: string
  careersIntro: string
  careers: GuideCareer[]
  faqHeading: string
  faqIntro: string
  faqs: GuideFAQ[]
  // CTAs / related
  relatedLinks: GuideRelatedLink[]
}

export const guides: Guide[] = [
  {
    slug: 'online-christian-colleges',
    title: 'Online Christian Colleges',
    metaTitle:
      'Best Online Christian Colleges & Universities | OnlineCollegeGuide',
    metaDescription:
      'Compare accredited online Christian colleges and faith-based universities. Explore Bible, ministry, theology, and traditional degrees grounded in Christian values.',
    breadcrumbLabel: 'Online Christian Colleges',
    heroEyebrow: 'A Guide To',
    heroHeadline: 'Online Christian Colleges & Universities',
    heroDescription:
      'Earn a respected, accredited degree from a faith-based university — on your schedule. Compare top online Christian colleges offering Bible, ministry, business, nursing, psychology, and more.',
    focusValue: '13', // Religious Studies
    defaultSpecialization: '1300', // All Religious Studies Programs (General)
    sidebarChecklistTitle: 'What to Look For',
    sidebarChecklist: [
      'Regional or national accreditation',
      'Statement of faith that aligns with you',
      'Flexible online course formats',
      'Transfer credit & military-friendly policies',
      'Affordable tuition with faith-based aid',
      'Chapel, mentorship & community life online',
    ],
    overviewHeading: 'What is an Online Christian College?',
    overviewParagraphs: [
      'An online Christian college is an accredited university that integrates a biblical worldview into every program — from theology and ministry to business, nursing, education, and counseling. Students earn the same kinds of degrees offered at secular institutions, but coursework is taught through the lens of Christian values, ethics, and Scripture.',
      'Today, leading faith-based universities like Liberty, Grand Canyon, Colorado Christian, Regent, and Indiana Wesleyan offer fully online programs designed for working adults. You can study at your own pace, connect with faculty who share your faith, and apply what you learn to your church, community, or career — without setting foot on campus.',
      "Whether you're called to ministry or simply want your education rooted in your beliefs, an online Christian college can help you grow intellectually, professionally, and spiritually at the same time.",
    ],
    benefitsHeading: 'Why Choose an Online Christian College?',
    benefitsIntro:
      'Faith-based online programs offer the same flexibility as secular online schools — with the added benefit of community, mentorship, and a values-driven education.',
    benefits: [
      {
        icon: 'BookOpenIcon',
        title: 'Faith-Integrated Curriculum',
        description:
          'Every course — from accounting to psychology — is taught from a biblical worldview, helping you connect what you learn with what you believe.',
      },
      {
        icon: 'UsersIcon',
        title: 'Christian Community Online',
        description:
          'Virtual chapel services, prayer groups, mentorship, and discussion forums help you build real relationships with students and faculty who share your values.',
      },
      {
        icon: 'ClockIcon',
        title: 'Flexible for Working Adults',
        description:
          'Accelerated 8-week courses, asynchronous learning, and rolling start dates make it easy to balance school with ministry, family, and work.',
      },
      {
        icon: 'HeartIcon',
        title: 'Service-Minded Mission',
        description:
          'Christian universities prepare graduates not just for careers, but for lives of service — at home, in the workplace, and around the world.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited & Respected',
        description:
          'Top Christian colleges hold regional accreditation, meaning your degree is recognized by employers, graduate schools, and licensing boards.',
      },
      {
        icon: 'TrendingUpIcon',
        title: 'Affordable Tuition Options',
        description:
          'Many faith-based universities offer reduced online tuition, generous transfer credit, and scholarships for ministry, military, and first-time students.',
      },
    ],
    careersHeading: 'Careers You Can Pursue with a Christian College Degree',
    careersIntro:
      'A degree from a Christian college opens doors to ministry roles as well as a wide range of professional careers in business, healthcare, education, and counseling.',
    careers: [
      {
        title: 'Pastor or Ministry Leader',
        desc: 'Lead a congregation, plant a church, or serve in a specialized ministry role focused on teaching, worship, or outreach.',
        salary: '$45,000 - $75,000',
      },
      {
        title: 'Christian Counselor',
        desc: 'Help individuals, couples, and families navigate life challenges through faith-informed, evidence-based counseling.',
        salary: '$50,000 - $80,000',
      },
      {
        title: 'Missionary or Nonprofit Leader',
        desc: 'Serve communities at home or abroad through Christian nonprofits, relief organizations, and faith-based missions.',
        salary: '$40,000 - $70,000',
      },
      {
        title: 'Christian School Teacher',
        desc: 'Teach K-12 students in private Christian schools, integrating academics with biblical principles and character formation.',
        salary: '$42,000 - $65,000',
      },
      {
        title: 'Youth or Worship Pastor',
        desc: 'Disciple students or lead worship within a local church, often combining teaching, mentoring, and event leadership.',
        salary: '$38,000 - $60,000',
      },
      {
        title: 'Chaplain',
        desc: 'Provide spiritual care in hospitals, the military, prisons, or corporate settings as a credentialed chaplain.',
        salary: '$50,000 - $80,000',
      },
      {
        title: 'Faith-Based Business Leader',
        desc: 'Apply Christian ethics and stewardship to roles in management, finance, marketing, or entrepreneurship.',
        salary: '$60,000 - $110,000',
      },
      {
        title: 'Nonprofit or Church Administrator',
        desc: 'Manage operations, finances, and staff for churches, ministries, and Christian nonprofits.',
        salary: '$50,000 - $85,000',
      },
    ],
    faqHeading: 'Frequently Asked Questions',
    faqIntro:
      'Common questions about earning your degree from an online Christian college.',
    faqs: [
      {
        q: 'Are online Christian colleges accredited?',
        a: 'Yes. Most reputable online Christian colleges — such as Liberty University, Grand Canyon University, Regent University, and Colorado Christian University — hold regional accreditation from the same agencies that accredit secular universities. Some specialty Bible colleges also hold accreditation from the Association for Biblical Higher Education (ABHE) or the Transnational Association of Christian Colleges and Schools (TRACS).',
      },
      {
        q: 'Do I have to be Christian to attend an online Christian college?',
        a: 'No. While Christian colleges are rooted in faith, most welcome students from all backgrounds. Students are generally expected to respect the school’s statement of faith and community values, but coursework, chapel, and spiritual activities are designed to be a positive experience whether or not you identify as Christian.',
      },
      {
        q: 'What kinds of degrees can I earn online from a Christian university?',
        a: 'Beyond ministry, theology, and biblical studies, you can earn online degrees in business, nursing, education, psychology, counseling, criminal justice, IT, social work, and more. Every program integrates a Christian worldview, but the academic content is comparable to programs at secular institutions.',
      },
      {
        q: 'How much does an online Christian college cost?',
        a: 'Tuition varies widely. Many online Christian universities offer reduced online tuition rates — often between $300 and $500 per credit hour — along with scholarships for ministry, military, first responders, and transfer students. Federal financial aid is available at most regionally accredited Christian colleges.',
      },
      {
        q: 'Can I transfer my previous college credits?',
        a: 'Almost always, yes. Most online Christian colleges accept generous transfer credit — sometimes up to 75% of a bachelor’s degree — from prior coursework, military training, and approved certifications. This can significantly reduce the time and cost to graduate.',
      },
      {
        q: 'Will employers respect a degree from an online Christian college?',
        a: 'Yes. Employers care most about accreditation and the skills you bring to the role. A degree from a regionally accredited Christian university carries the same weight as one from a secular school, and many graduates find their faith-informed perspective is an asset in leadership, healthcare, education, and counseling roles.',
      },
    ],
    relatedLinks: [
      { label: 'Religious Studies Programs', href: '/degrees/liberal-arts' },
      { label: 'Counseling & Psychology', href: '/degrees/psychology' },
      { label: 'Education & Teaching', href: '/degrees/education' },
      {
        label: 'Colorado Christian University',
        href: '/colleges/colorado-christian-university',
      },
    ],
  },
  {
    slug: 'medical-billing-coding-colleges',
    title: 'Online Medical Billing & Coding Colleges',
    metaTitle:
      'Online Medical Billing & Coding Programs | OnlineCollegeGuide',
    metaDescription:
      'Compare accredited online medical billing and coding programs. Get matched by career goals, schedule, and budget.',
    breadcrumbLabel: 'Medical Billing & Coding Colleges',
    heroEyebrow: 'A Guide To',
    heroHeadline: 'Online Medical Billing & Coding Programs',
    heroDescription:
      'Learn to translate care into codes and clean claims. Compare accredited online billing and coding programs built for working adults, most finishing in under two years.',
    focusValue: '5', // Healthcare & Medical Assisting
    defaultSpecialization: '509', // Medical Billing & Coding
    sidebarChecklistTitle: 'What to Look For',
    sidebarChecklist: [
      'ABHES, CAHIIM, or regional accreditation',
      'Curriculum built around ICD-10 and CPT',
      'Certification exam prep included',
      'Externship or practice-coding experience',
      'Fully online, self-paced coursework',
      'Career services and job placement support',
    ],
    overviewHeading: 'What Does a Medical Biller and Coder Do?',
    overviewParagraphs: [
      'Medical billers and coders turn clinical documentation into the standardized codes that healthcare runs on. Coders read a provider’s notes and assign ICD-10 diagnosis codes and CPT procedure codes; billers use those codes to build claims, submit them to insurers, track payments, and resolve denials. It is detail-driven, rules-heavy administrative work that sits between clinical care and getting paid for it.',
      'Because the work is documentation and data rather than patient contact, it adapts well to online study and to remote employment. Programs are typically a diploma or certificate of roughly 10 to 12 months, or an associate degree of about 18 to 24 months. Coursework covers medical terminology, anatomy, coding systems, healthcare reimbursement, HIPAA and compliance, and electronic health records.',
      'Most programs are structured to prepare you for a certification exam after graduation — commonly the CPC from AAPC, or the CCA or CCS from AHIMA. Certification is not always required to be hired, but many employers list it as a preference or a condition of continued employment, so it is worth confirming which credential a program prepares you for before you enroll.',
    ],
    benefitsHeading: 'Why Choose an Online Billing & Coding Program?',
    benefitsIntro:
      'Billing and coding is one of the few healthcare paths with no clinical hours, which makes a fully online program a realistic fit around a current job.',
    benefits: [
      {
        icon: 'ClockIcon',
        title: 'Shorter Than a Degree Path',
        description:
          'Diploma programs commonly run 10 to 12 months and associate degrees 18 to 24 months, so you reach the job market well before a four-year timeline.',
      },
      {
        icon: 'GlobeIcon',
        title: 'No Clinical Rotations',
        description:
          'The work is administrative, so there are no hospital shifts or in-person labs to schedule. Some programs include a remote or local externship for practice coding.',
      },
      {
        icon: 'CheckCircleIcon',
        title: 'Certification-Aligned Curriculum',
        description:
          'Coursework is mapped to the CPC, CCA, CCS, or CBCS exams, and many schools build exam review — and sometimes a voucher — into the program.',
      },
      {
        icon: 'ShieldCheckIcon',
        title: 'Accredited Programs',
        description:
          'Look for ABHES or CAHIIM accreditation, or regional accreditation at a college. Accreditation affects federal aid eligibility and certification pathways.',
      },
      {
        icon: 'BookOpenIcon',
        title: 'Skills Employers Ask For By Name',
        description:
          'ICD-10-CM, CPT, HCPCS, HIPAA, and EHR systems appear in job postings across hospitals, physician practices, insurers, and third-party billing companies.',
      },
      {
        icon: 'TrendingUpIcon',
        title: 'A Path Into Health Information',
        description:
          'Coding is a common entry point into revenue cycle, auditing, compliance, and health information management, where an associate degree helps you advance.',
      },
    ],
    careersHeading: 'Careers You Can Pursue in Billing & Coding',
    careersIntro:
      'Graduates work anywhere claims are produced or reviewed. Pay ranges below are broad national estimates that vary considerably by employer, credential, and metro area — check current BLS data and local postings for your market.',
    careers: [
      {
        title: 'Medical Coder',
        desc: 'Read clinical documentation and assign ICD-10, CPT, and HCPCS codes so encounters are billed accurately and compliantly.',
        salary: '$40,000 - $60,000',
      },
      {
        title: 'Medical Biller',
        desc: 'Build and submit claims, post payments, and follow up on unpaid balances with insurers and patients.',
        salary: '$38,000 - $55,000',
      },
      {
        title: 'Medical Records Specialist',
        desc: 'Maintain, organize, and release health information while keeping records complete and HIPAA-compliant.',
        salary: '$40,000 - $58,000',
      },
      {
        title: 'Claims Processor',
        desc: 'Review submitted claims against coverage rules on the payer side, and process approvals, adjustments, and denials.',
        salary: '$38,000 - $55,000',
      },
      {
        title: 'Denial Management Specialist',
        desc: 'Analyze denied claims, prepare appeals, and trace root causes upstream so the same denials stop recurring.',
        salary: '$45,000 - $62,000',
      },
      {
        title: 'Revenue Cycle Specialist',
        desc: 'Support the full cycle from charge capture through collections, monitoring accuracy and financial performance.',
        salary: '$45,000 - $65,000',
      },
      {
        title: 'Coding Auditor',
        desc: 'Review coded charts for accuracy and compliance, and coach coders on documentation and guideline changes. Usually requires experience and certification.',
        salary: '$55,000 - $80,000',
      },
      {
        title: 'Health Information Technician',
        desc: 'Manage data quality within electronic health record systems and support reporting and compliance activities.',
        salary: '$42,000 - $60,000',
      },
    ],
    faqHeading: 'Frequently Asked Questions',
    faqIntro:
      'Common questions about earning a medical billing and coding credential online.',
    faqs: [
      {
        q: 'How long does an online medical billing and coding program take?',
        a: 'It depends on the credential. Diploma and certificate programs commonly take about 10 to 12 months of full-time study, while an associate degree usually takes 18 to 24 months. Part-time and self-paced formats extend that timeline but let you keep working. Ask each school for the program length in weeks and how many courses you would take at a time.',
      },
      {
        q: 'Can I really learn medical coding entirely online?',
        a: 'Yes. Coding and billing are documentation and data work rather than hands-on patient care, so the coursework translates well to an online format. Some programs add a practicum or externship where you code real or simulated charts under supervision, which can often be arranged remotely or with a local employer.',
      },
      {
        q: 'Do I need to be certified to get hired?',
        a: 'Certification is not universally required, but it is widely preferred and some employers require it within a set period after hire. The most commonly requested credentials are the CPC from AAPC and the CCA or CCS from AHIMA; the CBCS from NHA also appears in billing roles. Confirm which exam a program prepares you for, and whether exam review or a voucher is included.',
      },
      {
        q: 'How do I know a billing and coding program is accredited?',
        a: 'Accreditation means an independent agency reviewed the school’s academic quality. For this field, look for institutional accreditation from ABHES, programmatic accreditation from CAHIIM, or regional accreditation at a college. You can verify a school in the U.S. Department of Education or CHEA databases, or ask the school which agency accredits them before you enroll.',
      },
      {
        q: 'Can I get financial aid for a billing and coding program?',
        a: 'Often yes. If you attend an accredited school eligible for federal aid, you may qualify for federal student aid — including Pell Grants and federal loans — by submitting the FAFSA. Many schools also offer scholarships, employer tuition assistance, or payment plans. Aid depends on the school, your program, and your eligibility, not on whether the program is online. After you match, ask each school’s aid office what applies to your situation.',
      },
      {
        q: 'Is medical coding going to be automated away?',
        a: 'Computer-assisted coding and AI tools are already common, and they handle routine, well-documented encounters. What they do not resolve is ambiguous documentation, complex or specialty cases, audits, appeals, and compliance judgment — which is where coders increasingly spend their time. Employers still hire for the human review layer, and coders who understand auditing and denials tend to have the most durable roles.',
      },
      {
        q: 'What is the difference between billing and coding?',
        a: 'Coding is the translation step: reading clinical documentation and assigning the correct diagnosis and procedure codes. Billing is the financial step: using those codes to create a claim, submit it to the payer, post payments, and resolve denials. Many programs teach both, and in smaller practices one person often does both jobs.',
      },
    ],
    relatedLinks: [
      {
        label: 'Healthcare & Medical Assisting Degrees',
        href: '/degrees/healthcare',
      },
      {
        label: 'Online Medical Assistant Programs',
        href: '/article/online-medical-assistant-programs',
      },
      {
        label: 'Accredited Online Colleges',
        href: '/article/accredited-online-colleges',
      },
      {
        label: 'Ultimate Medical Academy',
        href: '/colleges/ultimate-medical-academy',
      },
    ],
  },
]

export function getGuideBySlug(slug: string): Guide | undefined {
  return guides.find((g) => g.slug === slug)
}
